/**
 * Motion helpers.
 *
 * The marketing site moves with GSAP; the app does not need it. Everything
 * here is requestAnimationFrame or a CSS class from ui.css, and every helper
 * answers to prefers-reduced-motion: with it set, values arrive at their final
 * state on the first render and nothing animates.
 */
import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from 'react';

const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';

function mediaQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(REDUCED_QUERY);
}

function subscribe(onChange: () => void) {
  const mql = mediaQuery();
  if (!mql) return () => {};
  // Safari < 14 only has addListener.
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }
  mql.addListener(onChange);
  return () => mql.removeListener(onChange);
}

/** Read once, outside React. For module-level chart defaults. */
export function prefersReducedMotion(): boolean {
  return mediaQuery()?.matches ?? false;
}

/**
 * Whether the user has asked for less motion. Read synchronously, unlike
 * Mantine's useReducedMotion, which reports `false` for the first render and
 * would let one animation start before it changed its mind.
 */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, prefersReducedMotion, () => false);
}

/** The site's 'lp-out' feel: quick off the mark, long gentle settle. */
export function easeOutExpo(t: number): number {
  return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

export interface CountUpOptions {
  /** Milliseconds. Default 900, which reads as deliberate without waiting. */
  duration?: number;
  /** Where the very first animation starts. Default 0. */
  from?: number;
}

/**
 * A number that counts to `target`.
 *
 * It animates when `target` changes and only then: a refetch that returns the
 * same figure does nothing, and a new period counts from the figure on screen
 * to the new one rather than from zero. Returns a float; format it.
 */
export function useCountUp(target: number, { duration = 900, from = 0 }: CountUpOptions = {}): number {
  const reduced = usePrefersReducedMotion();
  const [value, setValue] = useState(() => (reduced ? target : from));
  // The figure currently on screen, kept outside state so an interrupted
  // animation resumes from where it actually got to.
  const shown = useRef(value);

  useEffect(() => {
    if (reduced || !Number.isFinite(target) || duration <= 0) {
      shown.current = target;
      setValue(target);
      return;
    }
    const start = shown.current;
    if (start === target) return;

    const t0 = performance.now();
    let frame = 0;
    const tick = () => {
      const progress = Math.min(1, (performance.now() - t0) / duration);
      const next = progress >= 1 ? target : start + (target - start) * easeOutExpo(progress);
      shown.current = next;
      setValue(next);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration, reduced]);

  return reduced ? target : value;
}

/**
 * Props for an element that should rise in on mount, `index` places it in a
 * stagger. CSS-driven, so it runs once when the element mounts and never on a
 * re-render; ui.css switches it off under reduced motion.
 *
 *   <Card {...riseIn(2)}>
 */
export function riseIn(index = 0, className?: string): { className: string; style: CSSProperties } {
  return {
    className: className ? `hf-rise ${className}` : 'hf-rise',
    style: { '--hf-i': index } as CSSProperties,
  };
}

/** Stagger index as a style, for elements that carry their own hf-* class. */
export function staggerStyle(index: number, style?: CSSProperties): CSSProperties {
  return { ...style, '--hf-i': index } as CSSProperties;
}
